import type { PointerEvent as ReactPointerEvent } from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { PageSetup } from '../model';
import {
  fromPageUnit,
  gridSpacingOf,
  isLandscape,
  toPageUnit,
  withOrientation,
  withPreset,
  type PagePreset,
  type PageUnit
} from '../domain/page';
import { ESCAPE_PRIORITY, primaryModifierLabel, useEscapeAction } from './escapeArbiter';
import type { GridKind } from '../rendering/canvasGrid';

interface PaperViewportControlsProps {
  readonly zoomPercent: number;
  /** #807 -- the view was just fitted (Fit page/drawing/selection) and not zoomed since: the zoom
   *  menu reads "Fit", as in Google Docs. */
  readonly fitted: boolean;
  readonly onZoomIn: () => void;
  readonly onZoomOut: () => void;
  /** #807 -- one of the zoom menu's preset levels (percent), about the view's centre. */
  readonly onZoomTo: (percent: number) => void;
  // #772 -- Fit page / Fit drawing (incl. overflow) / Fit selection / actual size (100%, page
  // centred).
  readonly onFitPage: () => void;
  readonly onFitDrawing: () => void;
  readonly fitDrawingDisabled: boolean;
  readonly onFitSelection: () => void;
  readonly fitSelectionDisabled: boolean;
  readonly onActualSize: () => void;
  // #772 -- page setup popover (preset, orientation, size, unit); absent hides the button.
  readonly page?: { readonly value: PageSetup; readonly onChange: (page: PageSetup) => void };
  // #772 part 2 -- Show grid (a per-device toggle) and its line/dot style (set in Page setup).
  readonly grid?: {
    readonly visible: boolean;
    readonly onToggle: () => void;
    readonly kind: GridKind;
    readonly onKindChange: (kind: GridKind) => void;
    // #772 part 2b -- Snap to grid (per device), independent of showing the grid.
    readonly snap: boolean;
    readonly onSnapToggle: () => void;
    // #772 part 3 -- smart guides (per device, on by default) and the touch "place freely" control,
    // which bypasses snapping while held (the touch counterpart of Alt/Option).
    readonly guides: boolean;
    readonly onGuidesToggle: () => void;
    readonly bypassHeld: boolean;
    readonly onBypassHold: (held: boolean) => void;
  };
  // #762 -- Pen-only drawing (pen draws, fingers navigate); shown once a pen has been used.
  readonly penOnly?: { readonly active: boolean; readonly onToggle: () => void };
  /** #807 R4 -- groups the host's toolbar has no room for: hidden here, offered in its ⋯ menu. */
  readonly hiddenGroups?: ReadonlySet<ViewControlGroup>;
  /** Where (inside the open ⋯ menu) the hidden groups are offered; null while ⋯ is closed. */
  readonly overflowContainer?: HTMLElement | null;
  /** The ⋯ button: Page setup opened from the menu is anchored to it. */
  readonly overflowAnchor?: HTMLElement | null;
  /** Closes the ⋯ menu (after a menu item that opens something else, or a one-shot command). */
  readonly onOverflowClose?: () => void;
}

/** #807 R4 -- the view controls' groups, as the toolbar measures and overflows them. */
export type ViewControlGroup = 'pen' | 'zoom' | 'snapping' | 'page';
const NO_GROUPS: ReadonlySet<ViewControlGroup> = new Set();

// Material Symbols (Outlined) paths, inlined because this package can't depend on the app's
// <Icon>. Coordinate space 0 -960 960 960; colour follows currentColor. The surrounding
// buttons carry the accessible name, so each glyph is aria-hidden. See docs/icon-language.md.
type GlyphName =
  | 'expandMore'
  | 'panTool'
  | 'add'
  | 'remove'
  | 'fitScreen'
  | 'fitPage'
  | 'fitSelection'
  | 'stylus'
  | 'pageSetup'
  | 'landscape'
  | 'grid'
  | 'gridLines'
  | 'gridDots'
  | 'snapGrid'
  | 'guides'
  | 'placeFreely';

const GLYPHS: Record<GlyphName, string> = {
  expandMore: 'M480-344 240-584l43-43 197 197 197-197 43 43-240 240Z',
  guides:
    'M120-120v-80h80v-560h-80v-80h720v80h-80v560h80v80H120Zm160-80h160v-120H280v120Zm0-200h160v-160H280v160Zm0-240h160v-120H280v120Zm240 440h160v-280H520v280Zm0-360h160v-200H520v200Z',
  placeFreely:
    'M480-80 310-250l57-57 73 73v-206H235l73 72-58 58L80-480l169-169 57 57-72 72h206v-206l-73 73-57-57 170-170 170 170-57 57-73-73v206h205l-73-72 58-58 170 170-170 170-57-57 73-73H520v205l72-73 58 58L480-80Z',
  snapGrid:
    'M440-40v-167l-44 43-56-56 140-140 140 140-56 56-44-43v167h-80ZM220-340l-56-56 43-44H40v-80h167l-43-44 56-56 140 140-140 140Zm520 0L600-480l140-140 56 56-43 44h167v80H753l43 44-56 56Zm-260-80q-25 0-42.5-17.5T420-480q0-25 17.5-42.5T480-540q25 0 42.5 17.5T540-480q0 25-17.5 42.5T480-420Zm0-180L340-740l56-56 44 43v-167h80v167l44-43 56 56-140 140Z',
  gridLines:
    'M200-120v-80h-80v-80h80v-160h-80v-80h80v-160h-80v-80h80v-80h80v80h160v-80h80v80h160v-80h80v80h80v80h-80v160h80v80h-80v160h80v80h-80v80h-80v-80H520v80h-80v-80H280v80h-80Zm80-160h160v-160H280v160Zm240 0h160v-160H520v160ZM280-520h160v-160H280v160Zm240 0h160v-160H520v160Z',
  gridDots:
    'M240-160q-33 0-56.5-23.5T160-240q0-33 23.5-56.5T240-320q33 0 56.5 23.5T320-240q0 33-23.5 56.5T240-160Zm240 0q-33 0-56.5-23.5T400-240q0-33 23.5-56.5T480-320q33 0 56.5 23.5T560-240q0 33-23.5 56.5T480-160Zm240 0q-33 0-56.5-23.5T640-240q0-33 23.5-56.5T720-320q33 0 56.5 23.5T800-240q0 33-23.5 56.5T720-160ZM240-400q-33 0-56.5-23.5T160-480q0-33 23.5-56.5T240-560q33 0 56.5 23.5T320-480q0 33-23.5 56.5T240-400Zm240 0q-33 0-56.5-23.5T400-480q0-33 23.5-56.5T480-560q33 0 56.5 23.5T560-480q0 33-23.5 56.5T480-400Zm240 0q-33 0-56.5-23.5T640-480q0-33 23.5-56.5T720-560q33 0 56.5 23.5T800-480q0 33-23.5 56.5T720-400ZM240-640q-33 0-56.5-23.5T160-720q0-33 23.5-56.5T240-800q33 0 56.5 23.5T320-720q0 33-23.5 56.5T240-640Zm240 0q-33 0-56.5-23.5T400-720q0-33 23.5-56.5T480-800q33 0 56.5 23.5T560-720q0 33-23.5 56.5T480-640Zm240 0q-33 0-56.5-23.5T640-720q0-33 23.5-56.5T720-800q33 0 56.5 23.5T800-720q0 33-23.5 56.5T720-640Z',
  grid: 'M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm0-80h133v-133H200v133Zm213 0h134v-133H413v133Zm214 0h133v-133H627v133ZM200-413h133v-134H200v134Zm213 0h134v-134H413v134Zm214 0h133v-134H627v134ZM200-627h133v-133H200v133Zm213 0h134v-133H413v133Zm214 0h133v-133H627v133Z',
  pageSetup:
    'M320-240h320v-80H320v80Zm0-160h320v-80H320v80ZM240-80q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h320l240 240v480q0 33-23.5 56.5T720-80H240Zm280-520v-200H240v640h480v-440H520ZM240-800v200-200 640-640Z',
  landscape:
    'M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm0-80h640v-480H160v480Zm0 0v-480 480Z',
  stylus:
    'M167-120q-21 5-36.5-10.5T120-167l40-191 198 198-191 40Zm191-40L160-358l458-458q23-23 57-23t57 23l84 84q23 23 23 57t-23 57L358-160Zm317-600L261-346l85 85 414-414-85-85Z',
  panTool:
    'M402-40q-27 0-51.5-12.5T311-88L54-468l20-16q17-15 39.5-19t44.57 13.19L280-397v-413q0-12.75 8.68-21.38 8.67-8.62 21.5-8.62 12.82 0 21.32 8.62 8.5 8.63 8.5 21.38v528L166-410l195 288q7 11 17.5 16.5T402-100h288q38 0 64-26t26-64v-580q0-12.75 8.68-21.38 8.67-8.62 21.5-8.62 12.82 0 21.32 8.62 8.5 8.63 8.5 21.38v580q0 63-43.5 106.5T690-40H402Zm45-440v-410q0-12.75 8.68-21.38 8.67-8.62 21.5-8.62 12.82 0 21.32 8.62 8.5 8.63 8.5 21.38v410h-60Zm167 0v-370q0-12.75 8.68-21.38 8.67-8.62 21.5-8.62 12.82 0 21.32 8.62 8.5 8.63 8.5 21.38v370h-60ZM473-290Z',
  add: 'M450-450H200v-60h250v-250h60v250h250v60H510v250h-60v-250Z',
  remove: 'M200-450v-60h560v60H200Z',
  fitScreen:
    'M820-610v-130H690v-60h130q24 0 42 18t18 42v130h-60Zm-740 0v-130q0-24 18-42t42-18h130v60H140v130H80Zm610 450v-60h130v-130h60v130q0 24-18 42t-42 18H690Zm-550 0q-24 0-42-18t-18-42v-130h60v130h130v60H140Zm60-120v-400h560v400H200Zm60-60h440v-280H260v280Zm0 0v-280 280Z',
  fitPage:
    'M720-80H240q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h480q33 0 56.5 23.5T800-800v640q0 33-23.5 56.5T720-80Zm-480-80h480v-640H240v640Zm0 0v-640 640Z',
  fitSelection:
    'M440-120v-400h400v80H576l264 264-56 56-264-264v264h-80Zm-160 0v-80h80v80h-80Zm-80-640h-80q0-33 23.5-56.5T200-840v80Zm80 0v-80h80v80h-80Zm160 0v-80h80v80h-80Zm160 0v-80h80v80h-80Zm160 0v-80q33 0 56.5 23.5T840-760h-80ZM200-200v80q-33 0-56.5-23.5T120-200h80Zm-80-80v-80h80v80h-80Zm0-160v-80h80v80h-80Zm0-160v-80h80v80h-80Zm640 0v-80h80v80h-80Z'
};

function Glyph({ name }: { readonly name: GlyphName }) {
  return (
    <svg
      viewBox="0 -960 960 960"
      width="1.25rem"
      height="1.25rem"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d={GLYPHS[name]} />
    </svg>
  );
}

// #807 part 1b (owner-approved mockup) -- the paper's own controls in the toolbar row, flat and
// grouped: Pen-only (with the pointer modes it follows) | the zoom menu | Snapping, Place freely
// and Page setup. Pan moved to the app's pointer-mode group; Zoom in/out, the three fits and 1:1
// live in the zoom menu; Show grid, Snap to grid and Smart guides in the Snapping popover.
export function PaperViewportControls({
  zoomPercent,
  fitted,
  onZoomIn,
  onZoomOut,
  onZoomTo,
  onFitPage,
  onFitDrawing,
  fitDrawingDisabled,
  onFitSelection,
  fitSelectionDisabled,
  onActualSize,
  penOnly,
  page,
  grid,
  hiddenGroups = NO_GROUPS,
  overflowContainer = null,
  overflowAnchor = null,
  onOverflowClose
}: PaperViewportControlsProps) {
  const { t } = useTranslation();
  const [pageOpen, setPageOpen] = useState(false);
  const pageButtonRef = useRef<HTMLButtonElement | null>(null);
  // Page setup opened from ⋯ (its own button hidden) is anchored to the ⋯ button instead.
  const overflowAnchorRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    overflowAnchorRef.current = overflowAnchor instanceof HTMLButtonElement ? overflowAnchor : null;
  }, [overflowAnchor]);
  const closePage = useCallback(() => setPageOpen(false), []);
  const hidden = (group: ViewControlGroup) => hiddenGroups.has(group);
  const zoomLabel = fitted ? t('paperViewportControls.fit') : `${zoomPercent}%`;
  const closeOverflow = () => onOverflowClose?.();

  const overflow =
    overflowContainer && hiddenGroups.size > 0
      ? createPortal(
          <div className="viewport-overflow" data-testid="view-controls-overflow">
            {hidden('pen') && penOnly ? (
              <button
                type="button"
                role="menuitemcheckbox"
                aria-checked={penOnly.active}
                className="viewport-menu-item"
                onClick={penOnly.onToggle}
              >
                <span className="viewport-menu-glyph" aria-hidden="true">
                  <Glyph name="stylus" />
                </span>
                <span className="viewport-menu-label">{t('paperViewportControls.penOnlyShort')}</span>
              </button>
            ) : null}
            {hidden('zoom') ? (
              <div role="group" aria-label={t('paperViewportControls.zoomMenu', { level: zoomLabel })}>
                <p className="viewport-overflow-heading">{t('paperViewportControls.zoomMenu', { level: zoomLabel })}</p>
                {(
                  [
                    { key: 'fit-page', glyph: 'fitPage', label: t('paperViewportControls.fitPage'), run: onFitPage },
                    {
                      key: 'fit-drawing',
                      glyph: 'fitScreen',
                      label: t('paperViewportControls.fitDrawing'),
                      run: onFitDrawing,
                      disabled: fitDrawingDisabled
                    },
                    {
                      key: 'fit-selection',
                      glyph: 'fitSelection',
                      label: t('paperViewportControls.fitSelection'),
                      run: onFitSelection,
                      disabled: fitSelectionDisabled
                    },
                    { key: 'actual', label: t('paperViewportControls.actualSize'), run: onActualSize },
                    {
                      key: 'in',
                      glyph: 'add',
                      label: t('paperViewportControls.zoomIn'),
                      run: onZoomIn,
                      keepOpen: true
                    },
                    {
                      key: 'out',
                      glyph: 'remove',
                      label: t('paperViewportControls.zoomOut'),
                      run: onZoomOut,
                      keepOpen: true
                    }
                  ] as const satisfies readonly {
                    key: string;
                    glyph?: GlyphName;
                    label: string;
                    run: () => void;
                    disabled?: boolean;
                    keepOpen?: boolean;
                  }[]
                ).map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    role="menuitem"
                    className="viewport-menu-item"
                    disabled={'disabled' in item ? item.disabled : false}
                    onClick={() => {
                      item.run();
                      if (!('keepOpen' in item)) closeOverflow();
                    }}
                  >
                    <span className="viewport-menu-glyph" aria-hidden="true">
                      {'glyph' in item ? <Glyph name={item.glyph} /> : null}
                    </span>
                    <span className="viewport-menu-label">{item.label}</span>
                  </button>
                ))}
              </div>
            ) : null}
            {hidden('snapping') && grid ? (
              <div role="group" aria-label={t('pageSetup.snapping')}>
                <p className="viewport-overflow-heading">{t('pageSetup.snapping')}</p>
                <SnappingSwitches grid={grid} />
              </div>
            ) : null}
            {hidden('page') && page ? (
              <button
                type="button"
                role="menuitem"
                className="viewport-menu-item"
                aria-haspopup="dialog"
                onClick={() => {
                  closeOverflow();
                  setPageOpen(true);
                }}
              >
                <span className="viewport-menu-glyph" aria-hidden="true">
                  <Glyph name="pageSetup" />
                </span>
                <span className="viewport-menu-label">{t('pageSetup.openEllipsis')}</span>
              </button>
            ) : null}
          </div>,
          overflowContainer
        )
      : null;

  return (
    <div
      className="viewport-controls"
      aria-label={t('paperViewportControls.ariaLabel')}
      onPointerDown={(event: ReactPointerEvent<HTMLDivElement>) => event.stopPropagation()}
    >
      {penOnly ? (
        <span className="viewport-group" data-toolbar-group="pen" hidden={hidden('pen')}>
          <button
            type="button"
            className={`viewport-pen-only${penOnly.active ? ' active' : ''}`}
            aria-label={t('paperViewportControls.penOnly')}
            title={t('paperViewportControls.penOnly')}
            aria-pressed={penOnly.active}
            onClick={penOnly.onToggle}
          >
            <Glyph name="stylus" />
          </button>
        </span>
      ) : null}
      <span className="viewport-group" data-toolbar-group="zoom" hidden={hidden('zoom')}>
        <span className="viewport-divider" aria-hidden="true" />
        <ZoomMenu
          zoomPercent={zoomPercent}
          fitted={fitted}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
          onZoomTo={onZoomTo}
          onFitPage={onFitPage}
          onFitDrawing={onFitDrawing}
          fitDrawingDisabled={fitDrawingDisabled}
          onFitSelection={onFitSelection}
          fitSelectionDisabled={fitSelectionDisabled}
          onActualSize={onActualSize}
        />
        {!fitDrawingDisabled ? (
          <button
            type="button"
            aria-label={t('paperViewportControls.fitDrawing')}
            title={t('paperViewportControls.fitDrawing')}
            onClick={onFitDrawing}
          >
            <Glyph name="fitScreen" />
          </button>
        ) : null}
      </span>
      <span className="viewport-divider" aria-hidden="true" />
      {grid ? (
        <span className="viewport-group" data-toolbar-group="snapping" hidden={hidden('snapping')}>
          <SnappingControl grid={grid} />
        </span>
      ) : null}
      {grid && (grid.snap || grid.guides) ? (
        <button
          type="button"
          className={`viewport-bypass${grid.bypassHeld ? ' active' : ''}`}
          aria-label={t('pageSetup.placeFreely')}
          title={t('pageSetup.placeFreelyHint')}
          aria-pressed={grid.bypassHeld}
          onPointerDown={(event) => {
            // Held until this pointer is released anywhere (a finger may slide off the button);
            // a window listener, as pointer capture can be lost when the paper re-renders.
            const pointerId = event.pointerId;
            grid.onBypassHold(true);
            const release = (up: PointerEvent) => {
              if (up.pointerId !== pointerId) return;
              grid.onBypassHold(false);
              window.removeEventListener('pointerup', release);
              window.removeEventListener('pointercancel', release);
            };
            window.addEventListener('pointerup', release);
            window.addEventListener('pointercancel', release);
          }}
          onContextMenu={(event) => event.preventDefault()}
        >
          <Glyph name="placeFreely" />
        </button>
      ) : null}
      {page ? (
        <span className="viewport-group viewport-page-setup" data-toolbar-group="page" hidden={hidden('page')}>
          <button
            ref={pageButtonRef}
            type="button"
            className={pageOpen ? 'active' : ''}
            aria-label={t('pageSetup.open')}
            title={t('pageSetup.open')}
            aria-expanded={pageOpen}
            aria-haspopup="dialog"
            onClick={() => setPageOpen((open) => !open)}
          >
            <Glyph name="pageSetup" />
          </button>
        </span>
      ) : null}
      {page && pageOpen ? (
        <PageSetupPopover
          anchorRef={hidden('page') ? overflowAnchorRef : pageButtonRef}
          page={page.value}
          onChange={page.onChange}
          onClose={closePage}
          {...(grid ? { gridKind: grid.kind, onGridKindChange: grid.onKindChange } : {})}
        />
      ) : null}
      {overflow}
    </div>
  );
}

// The zoom menu's preset levels (owner: as Google Docs, plus 400% -- the zoom limit).
const ZOOM_PRESETS = [50, 75, 90, 100, 125, 150, 200, 400] as const;

/**
 * #807 D1 -- one drop-down for every zoom and fit command. The button shows the zoom level ("Fit"
 * right after a fit) and keeps the "Zoom level" name on that readout. The menu: Fit page, Fit
 * drawing, Fit selection | 50-400% (100% = actual size, page centred, as the old 1:1) | Zoom in,
 * Zoom out. Arrow keys move through it; Escape or a press outside closes it.
 */
function ZoomMenu({
  zoomPercent,
  fitted,
  onZoomIn,
  onZoomOut,
  onZoomTo,
  onFitPage,
  onFitDrawing,
  fitDrawingDisabled,
  onFitSelection,
  fitSelectionDisabled,
  onActualSize
}: {
  readonly zoomPercent: number;
  readonly fitted: boolean;
  readonly onZoomIn: () => void;
  readonly onZoomOut: () => void;
  readonly onZoomTo: (percent: number) => void;
  readonly onFitPage: () => void;
  readonly onFitDrawing: () => void;
  readonly fitDrawingDisabled: boolean;
  readonly onFitSelection: () => void;
  readonly fitSelectionDisabled: boolean;
  readonly onActualSize: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const close = useCallback(() => setOpen(false), []);
  const label = fitted ? t('paperViewportControls.fit') : `${zoomPercent}%`;
  return (
    <div className="viewport-zoom">
      <button
        ref={buttonRef}
        type="button"
        className={`viewport-zoom-button${open ? ' active' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('paperViewportControls.zoomMenu', { level: label })}
        title={t('paperViewportControls.zoomMenu', { level: label })}
        onClick={() => setOpen((value) => !value)}
      >
        <output aria-label={t('paperViewportControls.zoomLevel')}>{label}</output>
        <Glyph name="expandMore" />
      </button>
      {open ? (
        <ZoomMenuList
          anchorRef={buttonRef}
          onClose={close}
          items={[
            { key: 'fit-page', glyph: 'fitPage', label: t('paperViewportControls.fitPage'), onSelect: onFitPage },
            {
              key: 'fit-drawing',
              glyph: 'fitScreen',
              label: t('paperViewportControls.fitDrawing'),
              shortcut: '⇧1',
              onSelect: onFitDrawing,
              disabled: fitDrawingDisabled
            },
            {
              key: 'fit-selection',
              glyph: 'fitSelection',
              label: t('paperViewportControls.fitSelection'),
              shortcut: '⇧2',
              onSelect: onFitSelection,
              disabled: fitSelectionDisabled
            },
            'separator',
            ...ZOOM_PRESETS.map((percent) => ({
              key: `zoom-${percent}`,
              label: percent === 100 ? t('paperViewportControls.actualSize') : `${percent}%`,
              checked: !fitted && zoomPercent === percent,
              ...(percent === 100 ? { shortcut: `${primaryModifierLabel()}0` } : {}),
              onSelect: percent === 100 ? onActualSize : () => onZoomTo(percent)
            })),
            'separator',
            {
              key: 'zoom-in',
              glyph: 'add',
              label: t('paperViewportControls.zoomIn'),
              shortcut: `${primaryModifierLabel()}=`,
              onSelect: onZoomIn,
              keepOpen: true
            },
            {
              key: 'zoom-out',
              glyph: 'remove',
              label: t('paperViewportControls.zoomOut'),
              shortcut: `${primaryModifierLabel()}−`,
              onSelect: onZoomOut,
              keepOpen: true
            }
          ]}
        />
      ) : null}
    </div>
  );
}

interface MenuEntry {
  readonly key: string;
  readonly label: string;
  readonly glyph?: GlyphName;
  readonly shortcut?: string;
  readonly checked?: boolean;
  readonly disabled?: boolean;
  /** Zoom in/out stay open, so repeated steps don't need the menu reopened. */
  readonly keepOpen?: boolean;
  readonly onSelect: () => void;
}

function ZoomMenuList({
  anchorRef,
  onClose,
  items
}: {
  readonly anchorRef: RefObject<HTMLButtonElement | null>;
  readonly onClose: () => void;
  readonly items: readonly (MenuEntry | 'separator')[];
}) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const position = useAnchoredOverlay(anchorRef, rootRef, onClose, 'left');
  // Focus the first enabled item on open; arrows move between enabled items (menu pattern).
  useEffect(() => {
    rootRef.current?.querySelector<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)')?.focus();
  }, []);
  const move = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Home' && event.key !== 'End') return;
    event.preventDefault();
    const entries = [
      ...(rootRef.current?.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)') ?? [])
    ];
    if (entries.length === 0) return;
    const at = entries.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? entries.length - 1
          : (at + (event.key === 'ArrowDown' ? 1 : -1) + entries.length) % entries.length;
    entries[next]?.focus();
  };
  return createPortal(
    <div
      ref={rootRef}
      className="viewport-menu"
      role="menu"
      tabIndex={-1}
      aria-label={t('paperViewportControls.zoom')}
      data-testid="zoom-menu"
      onKeyDown={move}
      style={position ? { left: position.left, top: position.top } : { visibility: 'hidden' }}
    >
      {items.map((item, index) =>
        item === 'separator' ? (
          <hr key={`separator-${index}`} className="viewport-menu-separator" />
        ) : (
          <button
            key={item.key}
            type="button"
            role={item.checked === undefined ? 'menuitem' : 'menuitemradio'}
            {...(item.checked === undefined ? {} : { 'aria-checked': item.checked })}
            className="viewport-menu-item"
            disabled={item.disabled}
            onClick={() => {
              item.onSelect();
              if (!item.keepOpen) {
                onClose();
                anchorRef.current?.focus();
              }
            }}
          >
            <span className="viewport-menu-glyph" aria-hidden="true">
              {item.glyph ? <Glyph name={item.glyph} /> : null}
            </span>
            <span className="viewport-menu-label">{item.label}</span>
            {item.shortcut ? (
              <kbd className="viewport-menu-shortcut" aria-hidden="true">
                {item.shortcut}
              </kbd>
            ) : null}
          </button>
        )
      )}
    </div>,
    document.body
  );
}

/**
 * #807 D2/D7 -- Snap to grid, Smart guides and Show grid behind one Snapping button. The button
 * reads "on" (selected background and a dot) while snapping or guides are on, so the state shows
 * without opening it. The switches keep their own names, so they read the same as the old buttons.
 */
function SnappingControl({ grid }: { readonly grid: NonNullable<PaperViewportControlsProps['grid']> }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => setOpen(false), []);
  const on = grid.snap || grid.guides;
  const state = t(on ? 'pageSetup.snappingOn' : 'pageSetup.snappingOff');
  return (
    <div className="viewport-snapping">
      <button
        ref={buttonRef}
        type="button"
        className={`viewport-snapping-button${on ? ' viewport-snapping-on' : ''}${open ? ' active' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${t('pageSetup.snapping')}: ${state}`}
        title={`${t('pageSetup.snapping')}: ${state}`}
        onClick={() => setOpen((value) => !value)}
      >
        <Glyph name="snapGrid" />
      </button>
      {open ? <SnappingPopover anchorRef={buttonRef} rootRef={rootRef} onClose={close} grid={grid} /> : null}
    </div>
  );
}

function SnappingPopover({
  anchorRef,
  rootRef,
  onClose,
  grid
}: {
  readonly anchorRef: RefObject<HTMLButtonElement | null>;
  readonly rootRef: RefObject<HTMLDivElement | null>;
  readonly onClose: () => void;
  readonly grid: NonNullable<PaperViewportControlsProps['grid']>;
}) {
  const { t } = useTranslation();
  const position = useAnchoredOverlay(anchorRef, rootRef, onClose);
  useEffect(() => {
    rootRef.current?.querySelector<HTMLInputElement>('input')?.focus();
  }, [rootRef]);
  return createPortal(
    <div
      ref={rootRef}
      className="viewport-menu viewport-snapping-popover"
      role="dialog"
      aria-label={t('pageSetup.snapping')}
      data-testid="snapping-popover"
      style={position ? { left: position.left, top: position.top } : { visibility: 'hidden' }}
    >
      <SnappingSwitches grid={grid} />
    </div>,
    document.body
  );
}

/** The three snapping switches -- in the Snapping popover, or in ⋯ when Snapping has no room. */
function SnappingSwitches({ grid }: { readonly grid: NonNullable<PaperViewportControlsProps['grid']> }) {
  const { t } = useTranslation();
  const rows: readonly { key: string; label: string; hint?: string; checked: boolean; onToggle: () => void }[] = [
    {
      key: 'snap',
      label: t('pageSetup.snapToGrid'),
      hint: t('pageSetup.snapToGridHint'),
      checked: grid.snap,
      onToggle: grid.onSnapToggle
    },
    { key: 'guides', label: t('pageSetup.smartGuides'), checked: grid.guides, onToggle: grid.onGuidesToggle },
    { key: 'grid', label: t('pageSetup.showGrid'), checked: grid.visible, onToggle: grid.onToggle }
  ];
  return (
    <>
      {rows.map((row) => (
        <label key={row.key} className="viewport-switch" title={row.hint}>
          <span>{row.label}</span>
          <input
            type="checkbox"
            role="switch"
            aria-checked={row.checked}
            checked={row.checked}
            onChange={row.onToggle}
          />
        </label>
      ))}
    </>
  );
}

const PRESETS: readonly PagePreset[] = ['letter', 'a4', 'slide-16-9', 'custom'];

// #772 -- page setup (docs/model/drawing-ui-viewport.md): preset, orientation, size and unit, applied
// live (each change is one Undo step in the host). Editing the size makes the page Custom. Content
// positions are never touched. Closes on Escape (arbiter) or a press outside.
// A size field edits a draft and commits on Enter or blur, so typing "210" is one undoable change
// rather than three (and never a momentary 2 mm page).
function PageSideInput({ value, onCommit }: { readonly value: number; readonly onCommit: (value: number) => void }) {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft !== null) onCommit(Number(draft));
    setDraft(null);
  };
  return (
    <input
      type="number"
      min={0}
      step="any"
      value={draft ?? value}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') commit();
      }}
    />
  );
}

// Portalled and fixed-positioned under its button: the view controls scroll horizontally on narrow
// screens, which would clip an absolutely positioned child.
const POPOVER_GUTTER = 16;

// #807 -- shared by Page setup, the zoom menu and the Snapping popover: placed under its button
// (right-aligned, kept on screen), closed by Escape (arbiter) or a press outside it and its button.
function useAnchoredOverlay(
  anchorRef: RefObject<HTMLButtonElement | null>,
  rootRef: RefObject<HTMLDivElement | null>,
  onClose: () => void,
  align: 'left' | 'right' = 'right'
) {
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const place = () => {
      const anchor = anchorRef.current?.getBoundingClientRect();
      const root = rootRef.current;
      if (!anchor || !root) return;
      const maxLeft = window.innerWidth - root.offsetWidth - POPOVER_GUTTER;
      const preferred = align === 'right' ? anchor.right - root.offsetWidth : anchor.left;
      setPosition({ left: Math.max(POPOVER_GUTTER, Math.min(preferred, maxLeft)), top: anchor.bottom + 6 });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [align, anchorRef, rootRef]);
  useEscapeAction(ESCAPE_PRIORITY.overlay, () => {
    onClose();
    return true;
  });
  useEffect(() => {
    const outside = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (rootRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('pointerdown', outside, true);
    return () => document.removeEventListener('pointerdown', outside, true);
  }, [anchorRef, onClose, rootRef]);
  return position;
}

function PageSetupPopover({
  anchorRef,
  page,
  onChange,
  onClose,
  gridKind,
  onGridKindChange
}: {
  readonly anchorRef: RefObject<HTMLButtonElement | null>;
  readonly gridKind?: GridKind;
  readonly onGridKindChange?: (kind: GridKind) => void;
  readonly page: PageSetup;
  readonly onChange: (page: PageSetup) => void;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const position = useAnchoredOverlay(anchorRef, rootRef, onClose);

  const unit = page.unit;
  const shown = (units: number) => Number(toPageUnit(units, unit).toFixed(unit === 'in' ? 2 : 1));
  const setSide = (side: 'width' | 'height', value: number) => {
    if (!(value > 0)) return;
    onChange({ ...page, preset: 'custom', [side]: fromPageUnit(value, unit) });
  };
  const landscape = isLandscape(page);

  return createPortal(
    <div
      ref={rootRef}
      className="page-setup-popover"
      role="dialog"
      aria-label={t('pageSetup.title')}
      style={position ? { left: position.left, top: position.top } : { visibility: 'hidden' }}
    >
      <label className="page-setup-row">
        {t('pageSetup.preset')}
        <select
          value={page.preset}
          onChange={(event) => {
            const preset = event.target.value as PagePreset;
            if (preset !== 'custom') onChange(withPreset(page, preset));
            else onChange({ ...page, preset: 'custom' });
          }}
        >
          {PRESETS.map((preset) => (
            <option key={preset} value={preset}>
              {t(`pageSetup.presets.${preset}`)}
            </option>
          ))}
        </select>
      </label>
      <div className="page-setup-row" role="radiogroup" aria-label={t('pageSetup.orientation')}>
        {([false, true] as const).map((wide) => (
          <button
            key={String(wide)}
            type="button"
            role="radio"
            aria-checked={landscape === wide}
            className={landscape === wide ? 'active' : ''}
            aria-label={t(wide ? 'pageSetup.landscape' : 'pageSetup.portrait')}
            title={t(wide ? 'pageSetup.landscape' : 'pageSetup.portrait')}
            onClick={() => onChange(withOrientation(page, wide))}
          >
            <Glyph name={wide ? 'landscape' : 'fitPage'} />
          </button>
        ))}
      </div>
      <div className="page-setup-row page-setup-size">
        <label>
          {t('pageSetup.width')}
          <PageSideInput value={shown(page.width)} onCommit={(value) => setSide('width', value)} />
        </label>
        <span aria-hidden="true">×</span>
        <label>
          {t('pageSetup.height')}
          <PageSideInput value={shown(page.height)} onCommit={(value) => setSide('height', value)} />
        </label>
        <select
          aria-label={t('pageSetup.unit')}
          value={unit}
          onChange={(event) => onChange({ ...page, unit: event.target.value as PageUnit })}
        >
          <option value="in">in</option>
          <option value="mm">mm</option>
        </select>
      </div>
      <div className="page-setup-row page-setup-grid">
        <label>
          {t('pageSetup.gridSpacing')}
          <PageSideInput
            value={shown(gridSpacingOf(page))}
            onCommit={(value) => value > 0 && onChange({ ...page, gridSpacing: fromPageUnit(value, unit) })}
          />
        </label>
        {gridKind && onGridKindChange ? (
          <div role="radiogroup" aria-label={t('pageSetup.gridStyle')} className="page-setup-row">
            {(['line', 'dot'] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                role="radio"
                aria-checked={gridKind === kind}
                className={gridKind === kind ? 'active' : ''}
                aria-label={t(kind === 'line' ? 'pageSetup.gridLines' : 'pageSetup.gridDots')}
                title={t(kind === 'line' ? 'pageSetup.gridLines' : 'pageSetup.gridDots')}
                onClick={() => onGridKindChange(kind)}
              >
                <Glyph name={kind === 'line' ? 'gridLines' : 'gridDots'} />
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {page.extent && (page.extent.columns > 1 || page.extent.rows > 1) ? (
        <p className="page-setup-hint" data-testid="page-grown">
          {t('pageSetup.grown', { columns: page.extent.columns, rows: page.extent.rows })}
        </p>
      ) : null}
      <p className="page-setup-hint">{t('pageSetup.hint')}</p>
    </div>,
    document.body
  );
}
